import React, { useRef, useState, useEffect, useCallback } from "react";

type ScrollShadowProps = {
	children?: React.ReactNode;
	className?: string;
	wrapperClassName?: string;
	horizontal?: boolean;
	vertical?: boolean;
};

const ScrollShadow = ({
	children = null,
	className = "",
	wrapperClassName = "",
	horizontal = false,
	vertical = false,
}: ScrollShadowProps) => {
	const scrollWrapper = useRef<HTMLDivElement>(null);
	const scrollContent = useRef<HTMLDivElement>(null);
	const [showBoxShadowLeft, setShowBoxShadowLeft] = useState(false);
	const [showBoxShadowRight, setShowBoxShadowRight] = useState(false);
	const [showBoxShadowBottom, setShowBoxShadowBottom] = useState(false);
	const [showBoxShadowTop, setShowBoxShadowTop] = useState(false);

	const updateBoxShadow = useCallback(() => {
		if (scrollWrapper.current) {
			if (horizontal) {
				const scrollPosLeft = scrollWrapper.current.scrollLeft;
				const scrollWidth =
					scrollWrapper.current.scrollWidth -
					scrollWrapper.current.clientWidth;

				setShowBoxShadowLeft(scrollPosLeft > 10);
				setShowBoxShadowRight(scrollPosLeft < scrollWidth - 10);
			}
			if (vertical) {
				const scrollPosTop = scrollWrapper.current.scrollTop;
				const scrollHeight =
					scrollWrapper.current.scrollHeight -
					scrollWrapper.current.clientHeight;
				setShowBoxShadowTop(scrollPosTop > 10);
				setShowBoxShadowBottom(scrollPosTop < scrollHeight - 10);
			}
		}
	}, [horizontal, vertical]);

	useEffect(() => {
		const wrapper = scrollWrapper.current;
		const content = scrollContent.current;

		if (wrapper && content) {
			// Add scroll event listener
			wrapper.addEventListener("scroll", updateBoxShadow);

			// Add resize event listener
			window.addEventListener("resize", updateBoxShadow);

			// Observe content changes
			const resizeObserver = new ResizeObserver(updateBoxShadow);
			resizeObserver.observe(content);

			// Initial update
			updateBoxShadow();

			// Cleanup
			return () => {
				wrapper.removeEventListener("scroll", updateBoxShadow);
				window.removeEventListener("resize", updateBoxShadow);
				resizeObserver.disconnect();
			};
		}
	}, [updateBoxShadow]);

	return (
		<div
			className={`scroll-shadow ${className || ""} ${
				horizontal ? "horizontal" : ""
			} ${vertical ? "vertical" : ""}`}
		>
			{showBoxShadowLeft && (
				<div className="scroll-shadow-indicator scroll-shadow-left" />
			)}
			{showBoxShadowRight && (
				<div className="scroll-shadow-indicator scroll-shadow-right" />
			)}
			{showBoxShadowTop && (
				<div className="scroll-shadow-indicator scroll-shadow-top" />
			)}
			{showBoxShadowBottom && (
				<div className="scroll-shadow-indicator scroll-shadow-bottom" />
			)}
			<div
				ref={scrollWrapper}
				className={`scroll-shadow-wrapper ${
					horizontal ? "horizontal" : ""
				} ${vertical ? "vertical" : ""} ${wrapperClassName || ""}`}
			>
				<div ref={scrollContent} className="scroll-shadow-content">
					{children}
				</div>
			</div>
		</div>
	);
};

export default ScrollShadow;
